import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Typography,
  Paper,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { format, subDays, startOfDay, isSameDay, parseISO } from 'date-fns';
import { Booking } from '../../types/booking';
import { roleContext } from '../../context/RoleContext';
import { UserRole } from '../../types/role';
import {
  getBookingsByShopId,
  getBookingsByBarberId,
  getShopSlotsById,
  getBarberSlotsByBarberId,
  getShopSlotsByShopIdAndBarberId,
} from '../../middleware/reservation_service/getRequests';
import { AxiosResponse } from 'axios';

interface ChartData {
  date: string;
  bookings: number;
  fullDate: Date;
}

interface ReservationsChartProps {
  onBarClick: (date: Date, bookings: Booking[]) => void;
}

const ReservationsChart: React.FC<ReservationsChartProps> = ({
  onBarClick,
}) => {
  const [timeRange, setTimeRange] = useState<number>(7);
  const [chartData, setChartData] = useState<ChartData[]>([]);
  const [bookingsMap, setBookingsMap] = useState<Map<string, Booking[]>>(
    new Map()
  );
  const [shopBarberBookingsMap, setShopBarberBookingMap] = useState<
    Map<string, Booking[]>
  >(new Map());
  const [loading, setLoading] = useState(false);
  const [activeBarIndex, setActiveBarIndex] = useState<number | null>(null);

  const role = useContext(roleContext);
  const partnerItem = localStorage.getItem('reg_partner_id');
  const partnerId = BigInt(
    partnerItem ? partnerItem.replace(/[^0-9]/g, '') : 0
  );
  const shopItem = localStorage.getItem('reg_shop_id');
  const shopId = BigInt(shopItem ? shopItem.replace(/[^0-9]/g, '') : 0);

  useEffect(() => {
    loadReservationsData();
  }, [timeRange, role, partnerId, shopId]);

  const loadReservationsData = async () => {
    setLoading(true);
    const getDayBooking = (booking: Booking, date: Date) => {
      if (!booking.slot_date) return false;
      try {
        const slotDate =
          typeof booking.slot_date === 'string'
            ? parseISO(booking.slot_date)
            : booking.slot_date;
        return isSameDay(slotDate, date);
      } catch (error) {
        console.error('Error parsing slot date:', error);
        return false;
      }
    };

    try {
      let bookingsResponse: AxiosResponse;
      let slotsResponse: AxiosResponse;

      if (role === UserRole.BARBER) {
        bookingsResponse = await getBookingsByBarberId(partnerId);
        slotsResponse = await getBarberSlotsByBarberId(partnerId);
      } else if (role === UserRole.SHOP_BARBER) {
        bookingsResponse = await getBookingsByShopId(shopId);
        slotsResponse = await getShopSlotsByShopIdAndBarberId(
          shopId,
          partnerId
        );
      } else {
        bookingsResponse = await getBookingsByShopId(shopId);
        slotsResponse = await getShopSlotsById(shopId);
      }

      const bookings: Booking[] = Array.isArray(bookingsResponse.data)
        ? bookingsResponse.data
        : [];

      const shopBarberBookings: Booking[] = bookings.filter(
        (booking) => Number(booking.barber_id) === Number(partnerId)
      );
      const slots = Array.isArray(slotsResponse.data) ? slotsResponse.data : [];

      // Create a map of slot_id to slot data
      const slotMap = new Map();
      slots.forEach((slot: any) => {
        slotMap.set(slot.id, {
          slot_date: slot.slot_date,
          start_time: slot.start_time,
        });
      });

      const enrichedBookings = bookings.map((booking) => {
        const slotData = slotMap.get(booking.slot_id);
        return {
          ...booking,
          slot_date: slotData?.slot_date,
          start_time: slotData?.start_time,
        };
      });

      const shopBarberEnrichedBookings = shopBarberBookings.map((booking) => {
        const slotData = slotMap.get(booking.slot_id);
        return {
          ...booking,
          slot_date: slotData?.slot_date,
          start_time: slotData?.start_time,
        };
      });

      // Group bookings by date
      const bookingsByDate = new Map<string, Booking[]>();
      const data: ChartData[] = [];
      const shopBarberBookingsByDate = new Map<string, Booking[]>();
      const shopBarberData: ChartData[] = [];

      // Generate dates for the selected range
      for (let i = timeRange - 1; i >= 0; i--) {
        const date = startOfDay(subDays(new Date(), i));
        const dateKey = format(date, 'yyyy-MM-dd');

        const dayBookings = enrichedBookings.filter((booking) => {
          return getDayBooking(booking, date);
        });

        const shopBarberDayBookings = shopBarberEnrichedBookings.filter(
          (booking) => {
            return getDayBooking(booking, date);
          }
        );

        if (role === UserRole.SHOP_BARBER) {
          shopBarberBookingsByDate.set(dateKey, shopBarberDayBookings);
          shopBarberData.push({
            date: timeRange > 7 ? format(date, 'MMM d') : format(date, 'EEE'),
            bookings: shopBarberDayBookings.length,
            fullDate: date,
          });
        } else {
          bookingsByDate.set(dateKey, dayBookings);
          data.push({
            date: timeRange > 7 ? format(date, 'MMM d') : format(date, 'EEE'),
            bookings: dayBookings.length,
            fullDate: date,
          });
        }
      }
      if (role === UserRole.SHOP_BARBER) {
        setChartData(shopBarberData);
        setShopBarberBookingMap(shopBarberBookingsByDate);
      } else {
        setChartData(data);
        setBookingsMap(bookingsByDate);
      }
    } catch (error) {
      console.error('Failed to load reservations data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTimeRangeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newRange: number | null
  ) => {
    if (newRange !== null) {
      setTimeRange(newRange);
      setActiveBarIndex(null);
    }
  };

  const handleBarClick = (data: any, index: number) => {
    setActiveBarIndex(index);
    const dateKey = format(data.fullDate, 'yyyy-MM-dd');
    if (role === UserRole.SHOP_BARBER) {
      const shopBarberBookings = shopBarberBookingsMap.get(dateKey) || [];
      onBarClick(data.fullDate, shopBarberBookings);
    } else {
      const bookings = bookingsMap.get(dateKey) || [];
      onBarClick(data.fullDate, bookings);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 2 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h6" fontWeight="bold">
          Reservations
        </Typography>
        <ToggleButtonGroup
          value={timeRange}
          exclusive
          onChange={handleTimeRangeChange}
          size="small"
        >
          <ToggleButton value={7}>7 Days</ToggleButton>
          <ToggleButton value={14}>14 Days</ToggleButton>
          <ToggleButton value={21}>21 Days</ToggleButton>
          <ToggleButton value={30}>30 Days</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <ResponsiveContainer width="100%" height={300}>
        <BarChart
          data={chartData}
          id={'reservations-chart'}
          onClick={(data) => {
            if (data && data.activeTooltipIndex !== undefined) {
              handleBarClick(
                chartData[data.activeTooltipIndex],
                data.activeTooltipIndex
              );
            }
          }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="date" />
          <YAxis allowDecimals={false} />
          <Tooltip />
          <Legend />
          <Bar dataKey="bookings" fill="#ce8b32" cursor="pointer">
            {chartData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={index === activeBarIndex ? '#8b5a1f' : '#ce8b32'}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Paper>
  );
};

export default ReservationsChart;
